import { LOGIN_ENDPOINT_INSIDE } from "@/config";
import { getAuthPaths } from "@/config/basePath";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import { fetchWithXSRF } from "@/lib/fetchWithXSRF";
import {
  clearLoginForm,
  loginFailure,
  loginStart,
  loginSuccess,
} from "@/store/Features/states/auth-management/authSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

/**
 * Custom hook for authentication actions
 * Provides all auth-related functionality with Redux integration
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { showError, showSuccess } = useAlert();
  const authState = useAppSelector((state) => state.auth);

  /**
   * Handle login process with Redux state management.
   * Uses fetchWithXSRF to include CSRF token in the request.
   * The server stores tokens in Redis and sets an HttpOnly session cookie.
   */
  const handleLogin = useCallback(async () => {
    dispatch(loginStart());

    try {
      const result = await fetchWithXSRF(LOGIN_ENDPOINT_INSIDE, {
        method: "POST",
        body: {
          email: authState.email,
          password: authState.password,
        },
      }).then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          showError(data.message || "Login failed");
          const error: any = new Error(
            data.message || data.error || "Login failed",
          );
          error.status = res.status;
          error.data = data;
          throw error;
        }
        return data;
      });

      // Check if login was successful
      if (!result.success) {
        throw new Error(result.message || "Login failed");
      }

      // With Redis sessions, tokens are stored server-side.
      // The session cookie is set automatically by the login API response.
      // We only dispatch the non-sensitive data to Redux.
      dispatch(loginSuccess(result?.data));

      // Navigate to dashboard
      const { DASHBOARD_URL } = getAuthPaths();
      showSuccess("Login successful!");

      // Small delay to ensure session cookie is set before redirect
      setTimeout(() => {
        router.push(DASHBOARD_URL);
        router.refresh();
      }, 100);
    } catch (error: any) {
      console.error("[useAuth] Login error:", error);

      let errorMessage = "Login failed. Please try again.";

      if (error?.status === 401) {
        errorMessage = "Invalid staff number or PIN.";
      } else if (error?.status === 400) {
        errorMessage = "Please check your credentials.";
      } else if (error?.status === 403) {
        errorMessage = error?.message || "Access denied.";
      } else if (error?.status === 422) {
        errorMessage = "Invalid input format.";
      } else if (error?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      }

      dispatch(loginFailure(errorMessage));
    }
  }, [dispatch, router, authState.email, authState.password]);

  /**
   * Handle input for email or password
   */
  const handleInput = useCallback(
    (
      key: string,
      current: string,
      maxLen: number,
      setter: (val: string) => void,
    ) => {
      if (key === "C") {
        dispatch(clearLoginForm());
      } else if (key === "←") {
        setter(current.slice(0, -1));
      } else if (/^[0-9]$/.test(key)) {
        const newVal = (current + key).slice(0, maxLen);
        setter(newVal);
      }
    },
    [dispatch],
  );

  return {
    // State
    ...authState,

    // Actions
    handleLogin,

    // Dispatch for other actions
    dispatch,
  };
};
